// lib/workstations/tab-list.js
// ═══════════════════════════════════════════════════════════
// The Workstation tabs — the PERSONAL active layer per the
// Platform Architecture doc (April 2026).
//
// Commit 97 (2026-04-27) — Tim trimmed to four tabs: Dashboard,
// QQQ, Think, Knowledge. The five universal personal tabs
// (Findings / Signal Read / Experience / Watchlist / Resources)
// were designed as the Workstation's general layer per the
// architecture doc, but for the QQQ workstation everything
// they would surface has been consolidated inside the QQQ tab
// itself (Library = timeline+curated, Journal = trades + calendar
// + coaching, Intelligence = patterns + ask). Showing the empty
// universal surfaces was decision fatigue, so they are removed.
// They can be re-introduced for non-trading domains via per-
// workstation config when those domains ship.
//
// ORDER IS LOAD-BEARING. Reads left-to-right as a flow:
//   Dashboard    — Personal Signal Matrix at a glance
//   QQQ          — the day-trading workstation (filtered)
//   Think        — Active Sessions (multi-thread chat)
//   Knowledge    — temp here, moves to Domain Intelligence
// ═══════════════════════════════════════════════════════════

export const WORKSTATION_TABS = [
  { key: 'dashboard',   label: 'Dashboard' },
  // QQQ tab — DAY-TRADING-WORKSTATIONS-ONLY. Filtered by
  // isTabEnabled() in config-schema.js so it only renders when
  // the workstation's slug is 'day-trading' (or its name matches).
  // Restored in Commit 19 after the parallel-session merge dropped
  // it from this list.
  { key: 'qqq',         label: 'QQQ' },
  { key: 'think',       label: 'Think' },
  { key: 'knowledge',   label: 'Knowledge' },
  // Catalyst tab — LONG-TERM-10X-ONLY. Surface for the Catalyst
  // Research methodology (penny + catalyst sustain-vs-fade
  // prediction product). Filtered by isTabEnabled() so it only
  // renders for workstations whose slug or name matches the
  // Long-Term 10x pattern. Reads from catalyst_hypotheses,
  // catalyst_predictions, catalyst_outcomes, catalyst_change_log.
  { key: 'catalyst',    label: 'Catalyst' },
  // Deals tab — WEALTH-BUILDING-WORKSTATIONS-ONLY. The Property
  // Deal Dossier ("Signal Read for an address"): comps + ARV +
  // deal math (70% rule, BRRRR refi, cash flow) + tax strategy.
  // Gated by isTabEnabled() in config-schema.js to wealth-building
  // workstations. Reads/writes workstation_property_dossiers.
  { key: 'deals',       label: 'Deals' },
  // Tools tab — WEALTH-BUILDING-WORKSTATIONS-ONLY. REI calculator suite
  // (ARV, 70% rule, MAO, rehab, rental, cap rate, DSCR, hard money, LTV,
  // refinance, holding cost, ROI, wholesale) + disclosed lender comparisons.
  // Config-driven from lib/wealth-building/tools/calculators.js. Gated in
  // config-schema.js alongside Deals + Close.
  { key: 'tools',       label: 'Tools' },
  // Close tab — the universal consultative SALES skill (Build / Rehearse /
  // Live). Designed to be available in any workstation; currently gated in
  // config-schema.js to wealth-building while it proves out.
  { key: 'close',       label: 'Close' },
];

export const TABS_BY_KEY = Object.fromEntries(
  WORKSTATION_TABS.map(t => [t.key, t])
);

/**
 * Preserved for compatibility with anything that still calls
 * groupedTabs(). With 6 tabs we don't render group separators
 * in the topbar anymore — everything lives in one flat group.
 */
export function groupedTabs(configFilter) {
  const tabs = WORKSTATION_TABS.filter((t) => !configFilter || configFilter(t));
  return tabs.length ? [{ group: 'All', tabs }] : [];
}

/**
 * Per-domain label override. Config can specify a custom label
 * for a tab via `tab_config[tabKey].label`. This is how:
 *   - Long-Term 10x renames Watchlist → "Companies"
 *   - A future Deal Intelligence WS could rename Watchlist → "Deals"
 */
export function tabLabelFor(tabKey, config) {
  const defaultLabel = TABS_BY_KEY[tabKey]?.label || tabKey;
  const override = config?.tab_config?.[tabKey]?.label;
  return override || defaultLabel;
}
